<!-- Bag (tab root, /tabs/bag): the shopping bag.

     A real kraft shopping bag, photographed, on the paper ground, with every
     coffee in it standing inside: the real pouches peek over the rim. It is
     three layers of one photo: the whole bag (its back and inside), then the
     pouches, then the bag again masked to its front panel, side and front
     handle, so the pouches stand BEHIND the front and in front of the back.
     Under it: the line items, the estimated total, and the handoff to
     BeanHub's own checkout. -->
<template>
  <IonPage>
    <AppHeader title="Your bag">
      <IonButtons v-if="lines.length" slot="end">
        <IonButton class="clear-bag" @click="clearAll">Clear bag</IonButton>
      </IonButtons>
    </AppHeader>

    <IonContent :fullscreen="true" class="bag-page">
      <AppLargeTitle title="Your bag" />

      <div class="bag-layout" :class="{ 'bag-layout--empty': !lines.length }">
        <!-- The bag itself. Decorative for a screen reader: every coffee in it is
           a row below, with the same actions. -->
        <div class="stage" :class="{ 'stage--empty': !lines.length, 'stage--few': lines.length === 2, 'stage--many': lines.length >= 3 }" aria-hidden="true">
          <div class="shop">
            <span class="shop__floor" />
            <img class="shop__layer shop__back" :src="BAG_SRC" alt="" draggable="false" decoding="async">
            <button
              v-for="p in pouches"
              :key="p.key"
              type="button"
              tabindex="-1"
              class="pouch"
              :class="{ 'pouch--lifted': liftedId === p.lineId, 'pouch--drop': dropping.has(p.key) }"
              :style="p.style"
              :aria-label="`${p.coffee.name}, ${p.grams}g, in your bag`"
              @click="onPick(p.lineId)"
            >
              <BagFront :coffee="p.coffee" :size="p.grams" variant="thumb" />
            </button>
            <svg v-if="pouches.length" class="shop__layer shop__shade" viewBox="0 0 666 1100" preserveAspectRatio="none">
              <defs>
                <linearGradient id="bag-rim-shade" gradientUnits="userSpaceOnUse" x1="410" y1="340" x2="382" y2="244">
                  <stop offset="0" stop-color="#46280f" stop-opacity="0.5" />
                  <stop offset="0.5" stop-color="#46280f" stop-opacity="0.14" />
                  <stop offset="1" stop-color="#46280f" stop-opacity="0" />
                </linearGradient>
              </defs>
              <polygon :points="OPENING" fill="url(#bag-rim-shade)" />
            </svg>
            <img class="shop__layer shop__front" :src="BAG_SRC" :style="frontMask" alt="" draggable="false" decoding="async">
            <span v-if="overflow > 0" class="shop__more">+{{ overflow }} more</span>
          </div>
        </div>

        <div class="bag-side">
          <div v-if="!lines.length" class="empty">
            <h2 class="empty__title">Your bag is empty. The wall is full.</h2>
            <p class="empty__sub">Pick up any coffee on the wall, choose a size, and it lands in here.</p>
            <IonButton expand="block" class="primary" @click="browse">Walk the shelves <span class="arrow" aria-hidden="true">→</span></IonButton>
          </div>

          <template v-else>
            <IonList lines="none" class="lines" aria-label="Coffees in your bag">
              <BagLine
                v-for="l in lines"
                :key="l.id"
                :ref="(c) => setRow(l.id, c)"
                :line="l"
                :lifted="liftedId === l.id"
                @remove="remove(l)"
              />
            </IonList>

            <ActionRail>
              <IonButton expand="block" class="primary" @click="handoffOpen = true">Check out on BeanHub · {{ aud(bagTotal) }}</IonButton>
              <!-- The count and the caveat ride in the rail, so nothing sits under it. -->
              <p class="rail-note">{{ countLabel }} · estimated · prices and stock confirmed on beanhub.com.au</p>
            </ActionRail>
          </template>
        </div>
      </div>

      <HandoffSheet :open="handoffOpen" :lines="lines" @close="handoffOpen = false" />
    </IonContent>
  </IonPage>
</template>

<script>
// The photo (public/wall/shopping-bag.webp) is 666 × 1100. Everything below is
// measured on it, in its pixels.
const W = 666;
const H = 1100;
// The front rim, left to right (the front panel's top edge, perspective slope).
const RIM = [[158, 413], [300, 379], [372, 354], [500, 324], [560, 302], [666, 267]];
// Going back into the bag, from the front-left corner to the back-left one.
const BACK = [-150, -96];
// Pixels per centimetre at the front panel (a 26 × 33 cm bag).
const PX_CM = 20.5;

// What covers the pouches: the front panel and left side (everything below the
// rim), and the front handle, whose legs are fixed inside the front panel.
const FRONT_MASK = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="none">`
  + `<path fill="#fff" d="M0 311 L133 345 L${RIM.map((p) => p.join(' ')).join(' L')} L${W} ${H} L0 ${H} Z"/>`
  + '<path fill="none" stroke="#fff" stroke-width="17" stroke-linecap="round" d="M334 380 C 324 262, 326 152, 368 104 C 398 68, 470 56, 506 100 C 526 128, 533 232, 528 322"/>'
  + '</svg>';
export const FRONT_MASK_URL = `url("data:image/svg+xml,${encodeURIComponent(FRONT_MASK)}")`;

const rimY = (x) => {
  for (let i = 1; i < RIM.length; i++) {
    const [x0, y0] = RIM[i - 1];
    const [x1, y1] = RIM[i];
    if (x <= x1) return y0 + ((Math.max(x, x0) - x0) / (x1 - x0)) * (y1 - y0);
  }
  return RIM[RIM.length - 1][1];
};

// Where pouches stand, for 1–6 of them, back to front (paint order).
//   t  across the bag, along the front rim (0 = left corner, 1 = right)
//   d  how far back (0 = against the front panel, 1 = against the back)
//   p  how much of the pouch shows above the rim in front of it
const SLOTS = {
  1: [{ t: 0.5, d: 0.42, p: 0.44 }],
  2: [{ t: 0.38, d: 0.6, p: 0.48 }, { t: 0.68, d: 0.24, p: 0.38 }],
  3: [{ t: 0.34, d: 0.66, p: 0.5 }, { t: 0.74, d: 0.62, p: 0.5 }, { t: 0.52, d: 0.2, p: 0.36 }],
  4: [{ t: 0.3, d: 0.7, p: 0.5 }, { t: 0.68, d: 0.66, p: 0.5 }, { t: 0.36, d: 0.22, p: 0.34 }, { t: 0.76, d: 0.18, p: 0.36 }],
  5: [{ t: 0.28, d: 0.74, p: 0.52 }, { t: 0.56, d: 0.72, p: 0.52 }, { t: 0.84, d: 0.64, p: 0.46 }, { t: 0.36, d: 0.24, p: 0.34 }, { t: 0.7, d: 0.2, p: 0.36 }],
  6: [{ t: 0.28, d: 0.76, p: 0.54 }, { t: 0.56, d: 0.74, p: 0.52 }, { t: 0.84, d: 0.66, p: 0.46 }, { t: 0.24, d: 0.3, p: 0.34 }, { t: 0.52, d: 0.24, p: 0.36 }, { t: 0.8, d: 0.18, p: 0.34 }],
};
export const MAX_POUCHES = 6;
// The bag's mouth: along the back rim, down the right gusset, back along the front rim.
const OPENING = [[0, 311], [170, 272], [300, 240], [485, 190], [666, 267], ...RIM.slice().reverse(), [133, 345]]
  .map((p) => p.join(',')).join(' ');
</script>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { IonPage, IonContent, IonList, IonButtons, IonButton, onIonViewDidEnter, onIonViewDidLeave } from '@ionic/vue';
import { AppHeader, AppLargeTitle, ActionRail, removeWithUndo } from '../core/ui/index.js';
import { haptic } from '../core/util.js';
import { bag, bagLines, bagCount, bagTotal, removeFromBag, clearBag, reducedMotion } from '../store.js';
import { isDemo } from '../core/demo.js';
import { frontOf, hash01 } from '../lib/wall-look.js';
import BagFront from '../components/BagFront.vue';
import BagLine from '../components/BagLine.vue';
import HandoffSheet from '../components/HandoffSheet.vue';

const frontMask = { WebkitMaskImage: FRONT_MASK_URL, maskImage: FRONT_MASK_URL };
const BAG_SRC = `${(import.meta.env.BASE_URL || '/').replace(/\/$/, '')}/wall/shopping-bag.webp`;

const router = useRouter();
// `demoBag()` in store.js seeds three coffees whenever /demo is open, with no
// regard for the QA fixture's `empty` vs `populated` request — so the "empty"
// state and the "populated" state showed the same three bags. This screen
// honours the fixture itself: same `appfactory:qa:seed` contract as
// `core/demo.js`, read once at module load.
const qaSeed = (() => {
  try { return localStorage.getItem('appfactory:qa:seed'); } catch { return null; }
})();
const lines = computed(() => ((isDemo.value && qaSeed === 'empty') ? [] : bagLines.value));
const aud = (n) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n);
const countLabel = computed(() => `${bagCount.value} ${bagCount.value === 1 ? 'bag' : 'bags'} of coffee`);

const handoffOpen = ref(false);
const liftedId = ref(null);

// One pouch per bag of coffee (a line of 2 stands two in the bag), capped.
const units = computed(() => lines.value.flatMap((l) =>
  Array.from({ length: l.qty }, (_, k) => ({ key: `${l.id}#${k}`, lineId: l.id, coffee: l.coffee, grams: l.grams || 250 }))));
const overflow = computed(() => Math.max(0, units.value.length - MAX_POUCHES));

// --- the pouches in the bag -----------------------------------------------------

const pct = (v, of) => `${((v / of) * 100).toFixed(3)}%`;
const pouches = computed(() => {
  // The newest go in last, on top: the last six units, painted back to front.
  const shown = units.value.slice(-MAX_POUCHES);
  const slots = SLOTS[shown.length] || [];
  return shown.map((u, i) => {
    const s = slots[i];
    const f = frontOf(u.coffee);
    const aspect = f?.aspect || 0.72;
    // True size: a 250 g pouch is ~20 cm tall, 500 g ~24, 1 kg ~30 (the bag is 33).
    const hCm = u.grams >= 1000 ? 30 : u.grams >= 500 ? 24 : 20;
    const hh = hCm * PX_CM * (1 - s.d * 0.12);            // a touch smaller further back
    const w = Math.min(hh * aspect, W * 0.5);
    const h = w / aspect;
    const x = RIM[0][0] + s.t * (RIM[RIM.length - 1][0] - RIM[0][0]) + s.d * BACK[0];
    // p of the pouch shows above the rim in front of it; further back stands higher.
    const top = rimY(x) + s.d * BACK[1] * 0.5 - s.p * h;
    const seed = hash01(u.key);
    const lean = (seed - 0.5) * 7;                          // ±3.5°, leaning on each other
    return {
      ...u,
      style: {
        left: pct(x - w / 2, W),
        top: pct(top, H),
        width: pct(w, W),
        '--lean': `${lean.toFixed(2)}deg`,
        '--shade': (1 - s.d * 0.16).toFixed(3),
        '--i': i,
      },
    };
  });
});

// A pouch drops in when it is new: while you watch, or the next time you
// come to the tab after adding it on the Shelf.
const seen = new Set();
const dropping = ref(new Set());
let visible = false;
let dropTimer = null;
function dropNew () {
  const fresh = pouches.value.map((p) => p.key).filter((k) => !seen.has(k));
  for (const p of pouches.value) seen.add(p.key);
  if (!fresh.length || reducedMotion.value) return;
  dropping.value = new Set(fresh);
  clearTimeout(dropTimer);
  dropTimer = setTimeout(() => { dropping.value = new Set(); }, 900);
}
onMounted(() => { for (const p of pouches.value) seen.add(p.key); });
onIonViewDidEnter(() => { visible = true; dropNew(); });
onIonViewDidLeave(() => { visible = false; liftedId.value = null; });
watch(pouches, () => { if (visible) dropNew(); });
onBeforeUnmount(() => clearTimeout(dropTimer));

const rows = new Map();
function setRow (id, c) { if (c) rows.set(id, c); else rows.delete(id); }

async function onPick (lineId) {
  liftedId.value = liftedId.value === lineId ? null : lineId;
  if (!liftedId.value) return;
  haptic('tick');
  await nextTick();
  const el = rows.get(lineId)?.$el;
  el?.scrollIntoView?.({ behavior: reducedMotion.value ? 'auto' : 'smooth', block: 'center' });
}

// --- actions --------------------------------------------------------------------

function remove (line) {
  const name = line.coffee.name;
  if (liftedId.value === line.id) liftedId.value = null;
  const revert = removeFromBag(line.id);
  haptic('pop');
  removeWithUndo({ text: `Removed ${name}.`, revert });
}

function clearAll () {
  liftedId.value = null;
  const revert = clearBag();
  haptic('pop');
  removeWithUndo({ text: 'Bag cleared.', revert });
}

function browse () {
  router.push('/tabs/shelf');
}

// Nothing in the bag means nothing to check out: close the sheet if it empties.
watch(() => bag.value.length, (n) => { if (!n) handoffOpen.value = false; });
</script>

<style scoped>
/* No bottom padding on the content, on purpose. `<ActionRail>` is sticky
   and IN FLOW at the end of the content, so at the bottom of the scroll it
   sits after the last row rather than over it: the last row always scrolls
   fully clear. Padding the scroll box does the opposite of what it looks like
   — a `bottom: 0` sticky element pins to the padding edge, so the rail is
   lifted off the bottom by the padding and floats mid-screen with rows
   passing under it (see core/ui/ActionRail.vue). The tab bar needs no
   clearance here either: IonTabs lays the outlet out ABOVE the bar, so the
   content already ends where the bar begins, and `--tabbar-clearance` is for
   what is presented from the app root (ion-toast), not page content. */
.bag-page {
  --background: var(--paper, #F4EFE6);
}

/* --- the stage: the bag on the paper --- */
.stage {
  --bag-h: min(21rem, 41vh);
  display: grid;
  place-items: center;
  padding: var(--sp-4) var(--gutter) var(--sp-3);
}
.stage--empty { --bag-h: min(20rem, 40vh); }
/* With coffees in it, the bag gives up height so the lines, the total and the
   checkout rail all fit on the first screen: nothing sits under the rail at rest. */
.stage:not(.stage--empty) { --bag-h: min(14rem, 27vh); padding-block: var(--sp-2) 0; }
/* The bag gives way as the list grows: the hero with one coffee, a header by three. */
.stage--few { --bag-h: min(12.5rem, 24vh); }
.stage--many { --bag-h: min(10rem, 19vh); }

.shop {
  position: relative;
  height: var(--bag-h);
  aspect-ratio: 666 / 1100;
  /* Nudged right: the bag's own weight is its front panel, not its box. */
  transform: translateX(4%);
}
.shop__layer {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  user-select: none;
  -webkit-user-drag: none;
  pointer-events: none;
}
/* The bag's shadow, cast down and to the left (the key light is upper right),
   drawn once, on the back layer only. */
.shop__back { filter: drop-shadow(-10px 12px 14px rgba(60, 40, 20, 0.16)); }
.shop__front {
  z-index: 2;
  -webkit-mask-size: 100% 100%;
  mask-size: 100% 100%;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
}
/* Contact shadow where the gusset meets the table. */
.shop__floor {
  position: absolute;
  left: -6%;
  right: 2%;
  bottom: -2.5%;
  height: 12%;
  border-radius: 50%;
  background: radial-gradient(closest-side, rgba(60, 40, 20, 0.34), rgba(60, 40, 20, 0.12) 60%, rgba(60, 40, 20, 0));
  transform: rotate(-7deg);
  filter: blur(4px);
}

/* Inside the bag it is darker toward the rim: a shade across the opening,
   strongest at the front rim, multiplied over the pouches and the inside of
   the back panel, under the front layer. */
.shop__shade {
  z-index: 1;
  mix-blend-mode: multiply;
}

/* A pouch in the bag. It faces the way the bag faces (the front panel's
   perspective is a skew of about −15°), leans a little on its neighbours, and
   is darker the further back it stands. */
.pouch {
  position: absolute;
  z-index: 1;
  display: block;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  transform-origin: 50% 100%;
  transform: skewY(-13deg) rotate(var(--lean));
  filter: brightness(var(--shade)) drop-shadow(-3px 3px 3px rgba(40, 26, 12, 0.34));
  transition: transform 220ms var(--ease), filter 220ms var(--ease);
  -webkit-tap-highlight-color: transparent;
}
.pouch :deep(.bag-front--thumb) { filter: none; }
.pouch--lifted {
  transform: translateY(-14%) skewY(-13deg) rotate(0deg);
  filter: brightness(1) drop-shadow(-5px 8px 7px rgba(40, 26, 12, 0.36));
}
.pouch--drop { animation: drop 620ms cubic-bezier(0.25, 0.9, 0.3, 1.08) both; animation-delay: calc(var(--i) * 40ms); }
@keyframes drop {
  from { transform: translateY(-70%) skewY(-13deg) rotate(calc(var(--lean) * -2)); opacity: 0; }
  30% { opacity: 1; }
  to { transform: skewY(-13deg) rotate(var(--lean)); }
}
@media (prefers-reduced-motion: reduce) {
  .pouch, .pouch--lifted { transition: none; }
  .pouch--drop { animation: none; }
}

.shop__more {
  position: absolute;
  z-index: 3;
  right: 4%;
  top: 34%;
  padding: var(--sp-1) var(--sp-3);
  border-radius: 999px;
  background: var(--mustard, #F2C230);
  color: var(--ink, #1F2A22);
  font-size: var(--text-xs);
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  box-shadow: 0 2px 6px rgba(40, 30, 20, 0.2);
}

/* --- empty --- */
.empty {
  padding: var(--sp-3) var(--gutter) var(--sp-7);
  text-align: center;
}
.empty__title {
  margin: 0 auto var(--sp-2);
  max-width: 18em;
  color: var(--ink, #1F2A22);
  font-family: var(--font-display, 'Bricolage Grotesque', 'Manrope', system-ui, sans-serif);
  font-size: var(--text-2xl);
  font-weight: 800;
  line-height: 1.08;
  letter-spacing: -0.025em;
  text-wrap: balance;
}
.empty__sub {
  margin: 0 auto var(--sp-5);
  max-width: 22rem;
  color: var(--ink-2, #3E4A41);
  line-height: 1.5;
}
.arrow { margin-left: var(--sp-2); color: var(--mustard, #F2C230); }

/* --- the lines --- */
.lines {
  margin: var(--sp-2) var(--gutter) 0;
  padding: 0;
  border-radius: var(--radius);
  overflow: hidden;
  background: var(--dock, #FAF7F1);
  box-shadow: 0 0 0 1px var(--line, #ECE6DC);
}
/* A row lifted from the bag scrolls clear of the sticky rail. */
.lines :deep(.bag-line) { scroll-margin-bottom: calc(var(--rail-height, var(--sp-8)) + var(--sp-3)); }
.lines :deep(.bag-line + .bag-line) { border-top: 1px solid var(--line, #ECE6DC); }


/* The one primary: ink pill, 52 tall, dock text. */
.primary {
  --background: var(--ink, #1F2A22);
  --background-hover: var(--ink, #1F2A22);
  --background-activated: var(--ink, #1F2A22);
  --background-focused: var(--ink, #1F2A22);
  --color: var(--dock, #FAF7F1);
  --border-radius: 999px;
  --box-shadow: none;
  height: calc(var(--sp-7) + var(--sp-1));
  margin: 0;
  font-family: var(--font-body, 'Manrope', system-ui, sans-serif);
  font-size: calc(var(--text-md) * 1.0625);
  font-weight: 800;
  letter-spacing: 0;
  text-transform: none;
  font-variant-numeric: tabular-nums;
  transition: transform 120ms var(--ease);
}
.primary:active { transform: scale(0.97); }
@media (prefers-reduced-motion: reduce) { .primary, .primary:active { transform: none; transition: none; } }
.bag-page :deep(.action-rail) { background: var(--paper, #F4EFE6); }

.clear-bag {
  --color: var(--ink, #1F2A22);
  text-transform: none;
  letter-spacing: normal;
  font-weight: 700;
}
/* Wide screens: the bag stands on the left, the lines and checkout sit beside it,
   so nothing is ever stacked under the sticky checkout. */
@media (min-width: 900px) {
  .bag-layout:not(.bag-layout--empty) {
    display: grid;
    grid-template-columns: minmax(0, 5fr) minmax(0, 6fr);
    align-items: start;
    gap: var(--sp-6);
    max-width: 1080px;
    margin: 0 auto;
    padding-inline: var(--gutter);
  }
  .bag-layout:not(.bag-layout--empty) .stage { --bag-h: min(24rem, 52vh); position: sticky; top: var(--sp-4); }
}
.rail-note {
  margin: var(--sp-2) 0 0;
  text-align: center;
  font-family: var(--font-body, 'Manrope', sans-serif);
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--muted, #5E6B60);
}
</style>
